import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';

const ProjectSwitcher = ({ currentProject, recentProjects = [], onProjectChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const defaultProjects = [
    { id: '1', name: 'E-commerce Store', lastAccessed: '2025-12-26', color: '#0F766E' },
    { id: '2', name: 'Portfolio Website', lastAccessed: '2025-12-25', color: '#F59E0B' },
    { id: '3', name: 'Blog Platform', lastAccessed: '2025-12-24', color: '#DC2626' }
  ];

  const projects = recentProjects?.length > 0 ? recentProjects : defaultProjects;
  const current = currentProject || projects?.[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event?.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleProjectSelect = (project) => {
    if (onProjectChange) {
      onProjectChange(project);
    }
    setIsOpen(false);
  };

  const getInitials = (name) => {
    return name?.split(' ')?.map(word => word?.[0])?.join('')?.toUpperCase()?.slice(0, 2);
  };

  return (
    <div className="relative px-4 py-3 border-b border-border" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-3 p-3 rounded-lg bg-muted hover:bg-muted/80 transition-all duration-250 group"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-heading font-semibold text-sm flex-shrink-0"
          style={{ backgroundColor: current?.color }}
        >
          {getInitials(current?.name)}
        </div>
        <div className="flex-1 text-left min-w-0">
          <div className="caption font-medium text-foreground truncate">
            {current?.name}
          </div>
          <div className="text-xs text-muted-foreground">
            Current project
          </div>
        </div>
        <Icon
          name={isOpen ? 'ChevronUp' : 'ChevronDown'}
          size={16}
          className="text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0"
        />
      </button>
      {isOpen && (
        <div className="absolute top-full left-4 right-4 mt-2 bg-popover border border-border rounded-lg shadow-lg overflow-hidden z-50">
          <div className="p-2 border-b border-border">
            <div className="caption font-medium text-muted-foreground px-3 py-2">
              Recent Projects
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {projects?.map((project) => {
              const isCurrentProject = project?.id === current?.id;
              return (
                <button
                  key={project?.id}
                  onClick={() => handleProjectSelect(project)}
                  className={`w-full flex items-center gap-3 p-3 hover:bg-muted transition-all duration-250 ${
                    isCurrentProject ? 'bg-muted' : ''
                  }`}
                  disabled={isCurrentProject}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-heading font-semibold text-sm flex-shrink-0"
                    style={{ backgroundColor: project?.color }}
                  >
                    {getInitials(project?.name)}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <div className="caption font-medium text-foreground truncate">
                      {project?.name}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Last accessed {project?.lastAccessed}
                    </div>
                  </div>
                  {isCurrentProject && (
                    <Icon name="Check" size={16} className="text-primary flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
          <div className="p-2 border-t border-border">
            <button
              onClick={() => {
                setIsOpen(false);
                window.location.href = '/project-dashboard';
              }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-primary hover:bg-primary/10 transition-all duration-250 caption font-medium"
            >
              <Icon name="Plus" size={16} />
              <span>Create New Project</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectSwitcher;